import type { AircraftState, EpochMs, FlightPhase } from './types.ts';

/**
 * eVTOL flight-phase inference (spec §5.2 "Phase + altitude"). Pure; used by buildWorldState, the
 * alert evaluator (takeoff/landing detection) and mark auto-tags.
 *
 * Inputs: groundspeed, vertical speed, height above an independently known ground reference, and the groundspeed / height trend over the last ~20 s of samples.
 *
 *   LANDED      height ≤ 40 ft, gs < 8 kt, |vs| < 200 fpm
 *   TAXI        height ≤ 40 ft, gs ≥ 8 kt (ground or hover taxi)
 *   HOVER       height > 40 ft, gs < 20 kt (includes vertical climb/descent)
 *   TRANSITION  20–60 kt and accelerating (or not clearly decelerating/descending)
 *   APPROACH    decelerating (< 60 kt), or ≥ 60 kt decelerating + descending below 800 ft
 *   CRUISE      wingborne ≥ 60 kt otherwise
 *   UNKNOWN     no groundspeed / altitude to judge by
 *
 * The result is `estimated: true` unless telemetry reported the phase.
 */

export const PHASE_GROUND_HEIGHT_FT = 40;
export const PHASE_LANDED_MAX_GS_KT = 8;
export const PHASE_HOVER_MAX_GS_KT = 20;
export const PHASE_WINGBORNE_MIN_GS_KT = 60;
export const PHASE_APPROACH_MAX_HEIGHT_FT = 800;
/** Trend window (s). */
export const PHASE_TREND_SEC = 20;
/** Groundspeed change over the trend window that reads as accelerating / decelerating (kt). */
export const PHASE_ACCEL_KT = 5;

export interface PhaseSample {
  t: EpochMs;
  altFt: number | null;
  gsKt: number | null;
  vsFpm?: number | null;
}

export interface PhaseHistory {
  /** Ground reference in the same vertical datum as state.altFt. A track minimum is not ground evidence. */
  baselineAltFt?: number | null;
  /** Recent samples (any order), ideally the last ~20 s. */
  samples?: readonly PhaseSample[];
  /** A phase reported by telemetry (wins; not estimated). */
  reportedPhase?: FlightPhase | null;
}

export interface PhaseResult {
  phase: FlightPhase;
  estimated: boolean;
}

export const AIRBORNE_PHASES: ReadonlySet<FlightPhase> = new Set(['HOVER', 'TRANSITION', 'CRUISE', 'APPROACH']);
export const GROUND_PHASES: ReadonlySet<FlightPhase> = new Set(['LANDED', 'TAXI']);

export const isAirbornePhase = (p: FlightPhase | null | undefined): boolean => p != null && AIRBORNE_PHASES.has(p);
export const isGroundPhase = (p: FlightPhase | null | undefined): boolean => p != null && GROUND_PHASES.has(p);

/** Short display labels (spec §5.2). */
export const FLIGHT_PHASE_LABELS: Record<FlightPhase, string> = {
  LANDED: 'LANDED',
  TAXI: 'TAXI',
  HOVER: 'HOVER',
  TRANSITION: 'TRANSITION',
  CRUISE: 'CRUISE',
  APPROACH: 'APPROACH',
  UNKNOWN: '—',
};

export function inferFlightPhase(
  state: Pick<AircraftState, 't' | 'gsKt' | 'vsFpm' | 'altFt'> & { phase?: FlightPhase | null },
  history?: PhaseHistory,
): PhaseResult {
  if (history?.reportedPhase) return { phase: history.reportedPhase, estimated: false };

  const samples = (history?.samples ?? []).filter((s) => s.t <= state.t && s.t >= state.t - (PHASE_TREND_SEC + 2) * 1000);
  const baseline = history?.baselineAltFt;
  const gs = state.gsKt;
  if (gs == null || !Number.isFinite(gs) || gs < 0 || state.altFt == null || !Number.isFinite(state.altFt)) {
    return { phase: 'UNKNOWN', estimated: true };
  }
  // Speed alone can support a broad wingborne state, never an arrival/landing claim. A first
  // observation is not evidence of ground elevation, even if the aircraft is slow or stationary.
  if (baseline == null || !Number.isFinite(baseline)) {
    return { phase: gs >= PHASE_WINGBORNE_MIN_GS_KT ? 'CRUISE' : 'UNKNOWN', estimated: true };
  }
  const height = state.altFt - baseline;
  if (height < -100) return { phase: 'UNKNOWN', estimated: true }; // inconsistent reference/data
  const h = Math.max(0, height);
  const vs = state.vsFpm != null && Number.isFinite(state.vsFpm) ? state.vsFpm : null;

  // Wingborne speed does not prove an approach just because altitude is near the reference.
  if (h <= PHASE_GROUND_HEIGHT_FT && gs >= PHASE_WINGBORNE_MIN_GS_KT) {
    return { phase: 'CRUISE', estimated: true };
  }
  if (h <= PHASE_GROUND_HEIGHT_FT) {
    if (vs == null) return { phase: 'UNKNOWN', estimated: true };
    if (Math.abs(vs) >= 200) {
      return { phase: gs < PHASE_HOVER_MAX_GS_KT ? 'HOVER' : vs < 0 ? 'APPROACH' : 'TRANSITION', estimated: true };
    }
    return { phase: gs < PHASE_LANDED_MAX_GS_KT ? 'LANDED' : 'TAXI', estimated: true };
  }
  if (gs < PHASE_HOVER_MAX_GS_KT) return { phase: 'HOVER', estimated: true };

  // Trend: groundspeed and height change across the window (oldest sample in the window → now).
  let dGs = 0;
  let dAlt = 0;
  const oldest = samples.reduce<PhaseSample | null>((a, s) => (a == null || s.t < a.t ? s : a), null);
  if (oldest && state.t - oldest.t >= 5000) {
    if (oldest.gsKt != null) dGs = gs - oldest.gsKt;
    if (oldest.altFt != null) dAlt = state.altFt - oldest.altFt;
  }
  const accelerating = dGs >= PHASE_ACCEL_KT;
  const decelerating = dGs <= -PHASE_ACCEL_KT;
  const descending = (vs != null && vs <= -200) || dAlt <= -60;

  if (gs < PHASE_WINGBORNE_MIN_GS_KT) {
    if (accelerating) return { phase: 'TRANSITION', estimated: true };
    if (decelerating) return { phase: 'APPROACH', estimated: true };
    if (descending && h < PHASE_APPROACH_MAX_HEIGHT_FT) return { phase: 'APPROACH', estimated: true };
    return { phase: 'TRANSITION', estimated: true };
  }
  if (decelerating && descending && h < PHASE_APPROACH_MAX_HEIGHT_FT) return { phase: 'APPROACH', estimated: true };
  return { phase: 'CRUISE', estimated: true };
}
